"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import { Sun, GraduationCap, Ghost, Palette, Moon } from "lucide-react"
import { useTheme } from "next-themes"
import { useQuery } from "@tanstack/react-query"
import { useCourse } from "@/components/providers/course-provider"
import { useAuth } from "@/hooks/use-auth"
import { themeService } from "@/services/themes"
import { loadSelectedSlug, selectCustomTheme } from "@/lib/custom-theme"

import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"


interface ModeToggleProps {
    className?: string
    showLabel?: boolean
}

export function ModeToggle({ className, showLabel = false }: ModeToggleProps) {
    const { theme, setTheme } = useTheme()
    const { setCourse } = useCourse()
    const { isAuthenticated } = useAuth()
    const [mounted, setMounted] = React.useState(false)
    const [selectedSlug, setSelectedSlug] = React.useState<string | null>(null)

    const activeTheme = theme || "light"
    const onCustom = activeTheme === "custom" || activeTheme === "custom-dark"

    const { data: customThemes } = useQuery({
        queryKey: ["customThemes"],
        queryFn: themeService.list,
        enabled: isAuthenticated,
        retry: false,
        staleTime: 1000 * 60 * 5,
    })

    const InlineIcon = activeTheme === "dark"
        ? Ghost
        : activeTheme === "dsat-school"
            ? GraduationCap
            : onCustom
                ? Palette
                : Sun

    React.useEffect(() => {
        setMounted(true)
        setSelectedSlug(loadSelectedSlug())
    }, [])

    /** Load a saved palette into the custom slot, then activate that slot. */
    const applyCustomTheme = (slug: string, mode: "light" | "dark") => {
        const record = customThemes?.find((t) => t.slug === slug)
        if (!record) return
        selectCustomTheme(slug, { light: record.light, dark: record.dark })
        setSelectedSlug(slug)
        setTheme(mode === "light" ? "custom" : "custom-dark")
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size={showLabel ? "sm" : "icon"}
                        className={cn(
                            "relative",
                            showLabel && "h-10 md:h-9 min-w-[48px] md:min-w-[80px] px-3 md:px-4 gap-2 rounded-md bg-background border-border text-foreground hover:bg-muted shadow-sm font-medium text-sm",
                            className
                        )}
                        suppressHydrationWarning
                    >
                        {showLabel ? (
                            <>
                                <InlineIcon className="h-4 w-4 shrink-0" />
                                <span className="hidden md:inline">Theme</span>
                            </>
                        ) : (
                            <>
                                <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 dsat-school:scale-0 custom:scale-0 custom-dark:scale-0" />
                                <Ghost className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100 dsat-school:scale-0 custom:scale-0 custom-dark:scale-0" />
                                <GraduationCap className="absolute h-[1.2rem] w-[1.2rem] scale-0 transition-all rotate-90 dark:rotate-90 dsat-school:rotate-0 dsat-school:scale-100 custom:scale-0 custom-dark:scale-0" />
                                <Palette className="absolute h-[1.2rem] w-[1.2rem] scale-0 transition-all rotate-90 custom:rotate-0 custom:scale-100 custom-dark:rotate-0 custom-dark:scale-100" />
                            </>
                        )}
                        <span className="sr-only">Toggle theme</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                    <DropdownMenuItem onClick={() => setTheme("light")} className="gap-2">
                        <Sun className="h-4 w-4" />
                        Light
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            setTheme("dark")
                        }}
                        className="gap-2"
                    >
                        <Ghost className="h-4 w-4" />
                        Ghost
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setTheme("dsat-school"); setCourse("DSAT") }} className="gap-2">
                        <GraduationCap className="h-4 w-4" />
                        DSAT School
                    </DropdownMenuItem>

                    {mounted && customThemes && customThemes.length > 0 && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                                Custom themes
                            </DropdownMenuLabel>
                            {customThemes.map((custom) => {
                                const isActive = onCustom && selectedSlug === custom.slug
                                return (
                                    <div
                                        key={custom.slug}
                                        className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5"
                                    >
                                        <span className="flex min-w-0 items-center gap-2 text-sm">
                                            <span
                                                className="h-3 w-3 shrink-0 rounded-full border"
                                                style={{ backgroundColor: custom.light?.primary }}
                                            />
                                            <span className={cn("truncate", isActive && "font-semibold")}>
                                                {custom.name}
                                            </span>
                                        </span>
                                        <span className="flex shrink-0 items-center gap-1">
                                            <button
                                                type="button"
                                                title={`${custom.name} — light`}
                                                onClick={() => applyCustomTheme(custom.slug, "light")}
                                                className={cn(
                                                    "rounded p-1 hover:bg-accent",
                                                    isActive && activeTheme === "custom" && "bg-accent"
                                                )}
                                            >
                                                <Sun className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                title={`${custom.name} — dark`}
                                                onClick={() => applyCustomTheme(custom.slug, "dark")}
                                                className={cn(
                                                    "rounded p-1 hover:bg-accent",
                                                    isActive && activeTheme === "custom-dark" && "bg-accent"
                                                )}
                                            >
                                                <Moon className="h-3.5 w-3.5" />
                                            </button>
                                        </span>
                                    </div>
                                )
                            })}
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    )
}
